import AsyncStorage from '@react-native-async-storage/async-storage';

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// Local union instead of the LightSdd enum to avoid a circular import with
// use-user-trustly-information, which consumes this store.
export type TrustlyInfoOverrideValues = {
    LIGHT_SDD: 'YES' | 'NO_REQUIRED' | 'NO' | 'IGNORE';
    TRUSTLY_VERIFIED: boolean;
    HAS_RESTRICTED_BALANCE: boolean;
    IS_IDPV: boolean;
};

export type TrustlyInfoOverrideToggleKey = Exclude<keyof TrustlyInfoOverrideValues, 'LIGHT_SDD'>;

// Defaults simulate the choice-screen scenario: restricted balance, no verification.
const defaultValues: TrustlyInfoOverrideValues = {
    LIGHT_SDD: 'NO',
    TRUSTLY_VERIFIED: false,
    HAS_RESTRICTED_BALANCE: true,
    IS_IDPV: false,
};

type TrustlyInfoOverrideStore = {
    enabled: boolean;
    values: TrustlyInfoOverrideValues;
    actions: {
        setEnabled: (enabled: boolean) => void;
        setLightSdd: (value: TrustlyInfoOverrideValues['LIGHT_SDD']) => void;
        toggleValue: (key: TrustlyInfoOverrideToggleKey) => void;
        reset: () => void;
    };
};

/**
 * Dev-only override for the `/auth/user/trustly-info` response so QA can simulate
 * any withdraw verification gate state from the Developer screen without
 * back-office setup. Consumed by useGetAuthUserTrustlyInfo.
 */
export const useTrustlyInfoOverrideStore = create<TrustlyInfoOverrideStore>()(
    persist(
        set => ({
            enabled: false,
            values: defaultValues,
            actions: {
                setEnabled: (enabled: boolean) => set({ enabled }),
                setLightSdd: (value: TrustlyInfoOverrideValues['LIGHT_SDD']) =>
                    set(state => ({ values: { ...state.values, LIGHT_SDD: value } })),
                toggleValue: (key: TrustlyInfoOverrideToggleKey) =>
                    set(state => ({ values: { ...state.values, [key]: !state.values[key] } })),
                reset: () => set({ enabled: false, values: defaultValues }),
            },
        }),
        {
            name: 'trustlyInfoOverride-storage',
            version: 1,
            storage: createJSONStorage(() => AsyncStorage),
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            partialize: ({ actions, ...rest }: TrustlyInfoOverrideStore) => rest,
        }
    )
);
